import { databases, appwriteConfig } from '../config/appwrite';
import { ID } from 'appwrite';

export interface Review {
  $id: string;
  autor: string;
  autorId: string;
  buchtitel: string;
  buchautor: string;
  rezensionstext: string;
  buchId: string;
  status: string;
  wortanzahl: number;
  bewertung: number | null;
  $createdAt: string;
  $updatedAt: string;
}

export interface CreateReviewData {
  autor: string;
  autorId: string;
  buchtitel: string;
  buchautor: string;
  rezensionstext: string;
  buchId: string;
  status: string;
  wortanzahl: number;
  bewertung: number | null;
}

class ReviewService {
  private databaseId = appwriteConfig.databaseId;
  private collectionId = 'reviews';

  async createReview(reviewData: CreateReviewData): Promise<Review> {
    try {
      const response = await databases.createDocument(
        this.databaseId,
        this.collectionId,
        ID.unique(),
        {
          autor: reviewData.autor,
          autorId: reviewData.autorId,
          buchtitel: reviewData.buchtitel,
          buchautor: reviewData.buchautor,
          rezensionstext: reviewData.rezensionstext,
          buchId: reviewData.buchId,
          status: reviewData.status,
          wortanzahl: reviewData.wortanzahl,
          bewertung: reviewData.bewertung
        }
      );

      return response as unknown as Review;
    } catch (error) {
      console.error('Fehler beim Erstellen der Rezension:', error);
      throw error;
    }
  }

  async getReviewsByUser(userId: string): Promise<Review[]> {
    try {
      const response = await databases.listDocuments(
        this.databaseId,
        this.collectionId,
        [`autorId.equal("${userId}")`]
      );

      return response.documents as unknown as Review[];
    } catch (error) {
      console.error('Fehler beim Laden der Rezensionen:', error);
      throw error;
    }
  }

  async getReviewsByBook(bookId: string): Promise<Review[]> {
    try {
      const response = await databases.listDocuments(
        this.databaseId,
        this.collectionId,
        [`buchId.equal("${bookId}")`]
      );

      return response.documents as unknown as Review[];
    } catch (error) {
      console.error('Fehler beim Laden der Rezensionen für Buch:', error);
      throw error;
    }
  }

  async getAllReviews(): Promise<Review[]> {
    try {
      const response = await databases.listDocuments(
        this.databaseId,
        this.collectionId
      );

      return response.documents as unknown as Review[];
    } catch (error) {
      console.error('Fehler beim Laden aller Rezensionen:', error);
      throw error;
    }
  }

  async updateReview(reviewId: string, updateData: Partial<CreateReviewData>): Promise<Review> {
    try {
      const response = await databases.updateDocument(
        this.databaseId,
        this.collectionId,
        reviewId,
        updateData
      );

      return response as unknown as Review;
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Rezension:', error);
      throw error;
    }
  }

  async deleteReview(reviewId: string): Promise<void> {
    try {
      await databases.deleteDocument(
        this.databaseId,
        this.collectionId,
        reviewId
      );
    } catch (error) {
      console.error('Fehler beim Löschen der Rezension:', error);
      throw error;
    }
  }

  async getReviewById(reviewId: string): Promise<Review> {
    try {
      const response = await databases.getDocument(
        this.databaseId,
        this.collectionId,
        reviewId
      );

      return response as unknown as Review;
    } catch (error) {
      console.error('Fehler beim Laden der Rezension:', error);
      throw error;
    }
  }
}

export const reviewService = new ReviewService();
